/**
 * Pop-up drawer with full details.
 *
 * Top bar offers browser-style navigation:
 *   <  (back through prior selections)
 *   >  (forward, after going back)
 *   ×  (close — clears history)
 *
 * History stack lives in the zustand store. Cross-links inside the drawer
 * call `setSelection` which auto-pushes onto the stack, so the back button
 * traces the user's path through the graph.
 *
 * Hover styling is injected once via a <style> block at the top of the
 * component, scoped to a few class names. Inline-style fallback colors keep
 * the look intact in non-hovered states.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { FeedbackTarget } from '../state/store';
import { isSelectionValid, useAppStore } from '../state/store';
import { feedbackConfigured } from './feedback-config';
import { useResponsivePanelLayout } from './useResponsivePanelLayout';
import { CLUSTER_COLORS, ZONE_COLORS, clusterColorForId } from '../zones/palette';
import type { Counter, MediaRef, MediaRefsIndex, Move, Position, Zone } from '../types/domain';
import { MoveTable } from './MoveTable';
import { ResizeHandle } from './ResizeHandle';
import { advantageStyle, isAdvantageTag, TAG_LABELS } from './tag-labels';
import {
  FONT,
  GLASS,
  LINE,
  LINE2,
  INK,
  INK2,
  INK3,
  BLUE,
  RADIUS,
  hexA,
} from './tokens';

type Props = {
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  degree: Map<string, number>;
  mediaRefs?: MediaRefsIndex | null;
};

// CSS injected once; provides hover affordances we can't write inline.
const HOVER_STYLES = `
.bjjmap-nav-btn {
  background: rgba(255,255,255,0.04);
  color: ${INK2};
  border: 1px solid ${LINE};
  border-radius: ${RADIUS.sm}px;
  padding: 6px 10px;
  cursor: pointer;
  font-size: 12px;
  letter-spacing: 0.05em;
  transition: background 120ms ease, border-color 120ms ease,
              color 120ms ease, transform 80ms ease;
  user-select: none;
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.bjjmap-nav-btn:hover:not(:disabled) {
  background: rgba(255,255,255,0.10);
  border-color: ${LINE2};
  color: ${INK};
}
.bjjmap-nav-btn:active:not(:disabled) {
  transform: translateY(1px);
  background: rgba(255,255,255,0.14);
}
.bjjmap-nav-btn:disabled {
  opacity: 0.32;
  cursor: not-allowed;
}

.bjjmap-row {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  padding: 7px 9px;
  border-radius: 5px;
  font-size: 13px;
  background: rgba(255,255,255,0.02);
  margin-bottom: 3px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: background 120ms ease, border-color 120ms ease,
              transform 80ms ease, padding-left 120ms ease;
  user-select: none;
}
.bjjmap-row:hover {
  background: rgba(202,211,223,0.09);
  border-color: rgba(202,211,223,0.18);
  padding-left: 12px;
}
.bjjmap-row:active {
  transform: translateY(1px);
}

/* NOTE: the .bjjmap-resize-handle styles that used to live here
   were removed when ResizeHandle.tsx became a shared component
   owning its own CSS (including the .h / .v orientation variants
   for side-drawer vs bottom-sheet). The leftover base block here
   was clobbering the .v variant's geometry in portrait mode --
   top:0; bottom:0; width:10px won the cascade against the .v
   variant's height:28px, so the handle was rendering as a 10x
   viewport strip at the top-left of the screen with the wrong
   cursor, NOT at the sheet's top edge. Landscape worked because
   the leftover base agreed with the .h variant's side-drawer
   geometry. */

.bjjmap-link {
  color: ${BLUE};
  cursor: pointer;
  text-decoration: underline;
  text-decoration-style: dotted;
  text-underline-offset: 2px;
  transition: color 120ms ease, text-decoration-color 120ms ease;
}
.bjjmap-link:hover {
  color: ${INK};
  text-decoration-style: solid;
}

/*
 * Sticky detail header used by PositionDetail / MoveDetail /
 * CounterDetail. The element pins to top: var(--bjjmap-drawer-nav-h)
 * — that CSS variable is set by the Drawer's ResizeObserver on the
 * existing top nav (switcher + back/forward), so the title sits
 * immediately under the nav row regardless of how tall that becomes.
 *
 * The ::after pseudo-element is the gradient fade: a 24px strip just
 * below the sticky element that fades from the drawer's solid bg color
 * down to transparent. Content scrolling up from below dissolves
 * through this fade band — the "obscuring animation" the user asked
 * for, without any JS scroll handler.
 */
.bjjmap-detail-header {
  position: sticky;
  top: var(--bjjmap-drawer-nav-h, 80px);
  z-index: 1;
  margin: 0 -18px;
  padding: 12px 18px 6px;
  background: ${GLASS.panel};
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
}
.bjjmap-detail-header::after {
  content: '';
  position: absolute;
  left: 0;
  right: 0;
  top: 100%;
  height: 22px;
  background: linear-gradient(
    to bottom,
    rgba(14, 16, 23, 0.92) 0%,
    rgba(14, 16, 23, 0.62) 45%,
    rgba(14, 16, 23, 0) 100%
  );
  pointer-events: none;
  /* Only show the gradient once content is actually scrolling under
     the sticky header — at the top of the scroll, the title sits at
     its natural position and there's nothing to obscure, so the band
     should be invisible. Drawer's scroll listener ramps the variable
     from 0 → 1 over the first ~30px of scroll and back to 0 when the
     user returns to the top. CSS transition gives the soft fade-in /
     fade-out. */
  opacity: var(--bjjmap-scroll-fade, 0);
  transition: opacity 200ms ease;
}
`;


export function Drawer({ positions, moves, counters, degree, mediaRefs }: Props) {
  const open = useAppStore((s) => s.drawerOpen);
  const sel = useAppStore((s) => s.selection);
  const setSelection = useAppStore((s) => s.setSelection);
  const setListViewOpen = useAppStore((s) => s.setListViewOpen);
  const back = useAppStore((s) => s.back);
  const forward = useAppStore((s) => s.forward);
  const drawerWidth = useAppStore((s) => s.drawerWidth);
  const drawerResizing = useAppStore((s) => s.drawerResizing);
  const openFeedback = useAppStore((s) => s.openFeedback);

  // Refs to the resizable DOM nodes. During a drag we mutate these
  // elements' inline styles directly (bypassing React/zustand) so the
  // resize is bound to pointer event rate, not to React's reconciliation
  // throughput. Only on pointerup do we commit the final width into the
  // store. Without this the drag was choppy because each pointermove
  // triggered a full re-render of App + Drawer + Hud's subscribers.
  const asideRef = useRef<HTMLElement>(null);
  const innerRef = useRef<HTMLDivElement>(null);
  // The sticky top nav (switcher + back/forward). Its height is
  // published as a CSS variable so the sticky detail header
  // (rendered by PositionDetail / MoveDetail / CounterDetail) knows
  // where to pin itself without hard-coded pixel math. ResizeObserver
  // re-publishes whenever the nav reflows (e.g. when the panel
  // narrows enough to wrap a row).
  const navRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = navRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return;
    const sync = () => {
      const h = el.getBoundingClientRect().height;
      // Scope on the inner scroll container so the variable doesn't
      // leak to the rest of the document — sticky `top:` resolves
      // against the nearest scroll container's coordinate system.
      const target = innerRef.current ?? el;
      target.style.setProperty('--bjjmap-drawer-nav-h', `${h}px`);
    };
    sync();
    const ro = new ResizeObserver(sync);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Scroll-driven gradient fade for the sticky detail header.
  // Ramps --bjjmap-scroll-fade from 0 → 1 over the first ~30px of
  // scroll and back to 0 when the user returns to the top. The
  // sticky header's ::after pseudo binds its opacity to this var so
  // the obscuring band fades in as scroll begins and fades out when
  // the title returns home. coalesced via rAF so a fast scroll
  // doesn't fire writes every microsecond.
  useEffect(() => {
    const el = innerRef.current;
    if (!el) return;
    let raf = 0;
    const update = () => {
      raf = 0;
      const t = Math.min(Math.max(el.scrollTop / 30, 0), 1);
      el.style.setProperty('--bjjmap-scroll-fade', String(t));
    };
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(update);
    };
    el.addEventListener('scroll', onScroll, { passive: true });
    update();
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  // canBack / canForward used to gate the in-body history buttons;
  // those moved into ViewSwitcher (which computes its own gating).
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // Esc behaviour is context-dependent. The drawer's handler
        // covers three cases (the list-view cases stay in ListView's
        // own listener):
        //   1. Detail view (drawer OPEN + selection) → clear the
        //      selection AND close the drawer. setSelection({kind:
        //      'none'}) flips drawerOpen=false via the store reducer.
        //   2. Drawer closed + selection present → clear the
        //      selection only; the drawer stays closed. Esc here
        //      drops the parked node without re-opening a panel the
        //      user just dismissed.
        //   3. Drawer closed + no selection → open the panel. Same
        //      default the edge caret uses: list view (since there's
        //      nothing for the detail panel to show).
        // We read fresh state at event-time via getState() so the
        // handler doesn't get pinned to stale closure values.
        e.preventDefault();
        const state = useAppStore.getState();
        const currentSel = state.selection;
        const drawerCurrentlyOpen = state.drawerOpen;
        if (currentSel.kind !== 'none') {
          // Both "drawer open + selection" and "drawer closed +
          // selection" collapse to the same action: clear the
          // selection. When the drawer was open, the store reducer
          // closes it as a side effect; when it was already closed,
          // nothing visible changes except the parked highlight.
          void drawerCurrentlyOpen;
          setSelection({ kind: 'none' });
        } else {
          // Drawer closed + nothing selected → open the panel.
          setListViewOpen(true);
        }
      } else if ((e.metaKey || e.altKey) && e.key === 'ArrowLeft') {
        e.preventDefault();
        back();
      } else if ((e.metaKey || e.altKey) && e.key === 'ArrowRight') {
        e.preventDefault();
        forward();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [setSelection, setListViewOpen, back, forward]);

  /** Friendly fallback when the selection points to an entity that
   *  no longer exists in the dataset — typically because it was
   *  removed between sessions. We avoid silently "no-op"ing the
   *  drawer (which used to render blank) so the user knows why the
   *  Detail content isn't there. The Detail tab guard above also
   *  hides itself in this case so the user lands on List by default. */
  const missingDataFallback = (kind: string, id: string) => (
    <div
      style={{
        padding: '24px 8px',
        textAlign: 'center',
        color: INK2,
      }}
    >
      <div style={{ fontSize: 14, fontWeight: 600, marginBottom: 6 }}>
        Couldn't find this {kind}.
      </div>
      <div style={{ fontSize: 12, opacity: 0.7 }}>
        It may have been renamed or removed. Pick another node from the
        map to keep exploring.
      </div>
      <div
        style={{
          fontSize: 10,
          opacity: 0.4,
          marginTop: 14,
          fontFamily: FONT.mono,
        }}
      >
        id: {id}
      </div>
    </div>
  );

  const content = useMemo(() => {
    // Per-node feedback callback, threaded into each detail component
    // so the "suggest an edit" button can live in the NODE header
    // (right of the name banner) rather than the tab-switcher row.
    // Undefined when the form isn't configured — the button hides.
    const feedbackFor = (t: FeedbackTarget) =>
      feedbackConfigured ? () => openFeedback(t) : undefined;

    if (sel.kind === 'position') {
      const p = positions.find((x) => x.id === sel.id);
      if (!p) return missingDataFallback('position', sel.id);
      const out = moves
        .filter((m) => m.from === p.id)
        .map((m) => ({ ...m, targetDegree: degree.get(m.to) ?? 0 }))
        .sort((a, b) => b.targetDegree - a.targetDegree);
      const incoming = moves
        .filter((m) => m.to === p.id)
        .map((m) => ({ ...m, srcDegree: degree.get(m.from) ?? 0 }))
        .sort((a, b) => b.srcDegree - a.srcDegree);
      return (
        <PositionDetail
          position={p}
          outgoing={out}
          incoming={incoming}
          positions={positions}
          moves={moves}
          counters={counters}
          media={mediaRefs?.positions[p.id] ?? []}
          onFeedback={feedbackFor({ kind: 'position', id: p.id, name: p.name })}
        />
      );
    }
    if (sel.kind === 'move') {
      const m = moves.find((x) => x.id === sel.id);
      if (!m) return missingDataFallback('move', sel.id);
      const cs = (m.counters ?? [])
        .map((cid) => counters.find((c) => c.id === cid))
        .filter(Boolean) as Counter[];
      return (
        <MoveDetail
          move={m}
          counters={cs}
          positions={positions}
          moves={moves}
          media={mediaRefs?.moves[m.id] ?? []}
          onFeedback={feedbackFor({ kind: 'move', id: m.id, name: m.name })}
        />
      );
    }
    if (sel.kind === 'counter') {
      const c = counters.find((x) => x.id === sel.id);
      const m = moves.find((x) => x.id === sel.moveId);
      if (!c || !m) return missingDataFallback('counter', sel.id);
      return (
        <CounterDetail
          counter={c}
          move={m}
          positions={positions}
          moves={moves}
          counters={counters}
          media={mediaRefs?.counters?.[c.id] ?? []}
          onFeedback={feedbackFor({ kind: 'counter', id: c.id, name: c.name })}
        />
      );
    }
    return null;
  }, [sel, positions, moves, counters, degree, mediaRefs, openFeedback]);

  // Resolve the current selection to a feedback target (kind + id +
  // (The per-node feedback target is now built inline in the content
  // useMemo above and passed to each detail component as onFeedback,
  // so the button can render inside the node header. The standalone
  // feedbackTarget memo that fed the old tab-row button is gone.)

  // Layout (open/close animation, side-vs-bottom orientation, slide-in
  // clip, surface anchoring) is owned by the shared hook so changes
  // propagate to all three right-pane / bottom-sheet panels at once.
  const { asideStyle, innerSurfaceStyle, bottomSheet } =
    useResponsivePanelLayout({
      open,
      drawerWidth,
      resizing: drawerResizing,
      slideInOnMount: true,
    });
  return (
    <>
      <style>{HOVER_STYLES}</style>
      <aside ref={asideRef} style={{ ...asideStyle, color: INK }}>
        {/* Visual surface — anchored to the appropriate edge by the
            hook so the open-animation clip reads as a slide-in. The
            background/border styles live HERE (per-component) so each
            panel keeps its own visual identity. */}
        <div
          ref={innerRef}
          style={{
            ...innerSurfaceStyle,
            background: GLASS.panel,
            backdropFilter: 'blur(14px)',
            overflowY: 'auto',
            overflowX: 'hidden',
            ...(bottomSheet
              ? {
                  borderTop: open
                    ? `1px solid ${LINE}`
                    : 'none',
                }
              : {
                  borderLeft: open
                    ? `1px solid ${LINE}`
                    : 'none',
                }),
          }}
        >
        {/* Top nav — two rows. Row 1 is the Detail/List switcher (the
            drawer is "Detail" view; toggling flips to the list panel
            with the current selection scrolled into view). Row 2 is
            history nav + close. Sticky so it survives long detail
            content scrolling.

            Tagged ref publishes its height to a CSS variable
            (--bjjmap-drawer-nav-h) so the sticky detail header below
            knows where to pin itself without hard-coded pixel math. */}
        <div
          ref={navRef}
          style={{
            position: 'sticky',
            top: 0,
            background: GLASS.panel,
            backdropFilter: 'blur(10px)',
            borderBottom: `1px solid ${LINE}`,
            zIndex: 2,
          }}
        >
          <div style={{ padding: '10px 14px' }}>
            {/* ViewSwitcher now includes universal back/forward to its
                right, so we no longer need a separate history row
                below the tabs. The 1/N counter is also gone — the
                back button is the affordance now. The per-node
                feedback button moved OUT of this tab row and into the
                detail node header (right of the name banner). */}
            <ViewSwitcher
              activeView="detail"
              hasSelection={isSelectionValid(sel, positions, moves, counters)}
            />
          </div>
        </div>

          <div style={{ padding: '14px 18px 32px' }}>{content}</div>
        </div>
      </aside>
      {/* Handle is rendered OUTSIDE the aside so the aside's overflow:hidden
          doesn't clip it. position:fixed via the CSS class — its inline
          `right` value tracks the drawer width so the handle stays at
          the boundary as the drawer animates / resizes. Live width is
          applied directly to aside + inner during the drag to skip
          React/R3F re-renders. */}
      <ResizeHandle
        open={open}
        applyLiveWidth={(size, axis = 'width') => {
          const cssV = `${size}px`;
          if (asideRef.current) {
            // Horizontal mode mutates width; vertical mode (bottom
            // sheet) mutates height. Either way the flexbox parent
            // sees the change immediately and the canvas pane
            // reflows in lockstep with the drag.
            asideRef.current.style[axis] = cssV;
          }
          if (innerRef.current && axis === 'width') {
            // Inner surface is right-anchored in side mode and needs
            // its own width update so the body stays the right size
            // while the aside grows. In bottom-sheet mode the inner
            // uses top:0/bottom:0 so it auto-fills — no mutation.
            innerRef.current.style.width = cssV;
          }
        }}
      />
    </>
  );
}

function PositionDetail({
  position,
  outgoing,
  incoming,
  positions,
  moves,
  counters,
  media,
  onFeedback,
}: {
  position: Position;
  outgoing: (Move & { targetDegree: number })[];
  incoming: (Move & { srcDegree: number })[];
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  media: MediaRef[];
  onFeedback?: () => void;
}) {
  const accent = ZONE_COLORS[position.zone as Zone];
  const setSelection = useAppStore((s) => s.setSelection);
  const setPreviewMoveId = useAppStore((s) => s.setPreviewMoveId);
  // Read-side of the hover link: when the user hovers a sub-arc in the
  // 3D scene the store's previewMoveId updates, and the matching row
  // in each MoveTable below gets highlighted via highlightedId.
  const previewMoveId = useAppStore((s) => s.previewMoveId);

  // Counters relevant to this position:
  //   - intercepting a move I can do from here (uke's response to my attack), and/or
  //   - landing here (counter.result === this position) — a way to arrive
  //     at this position via someone else's failed attempt.
  // Deduplicated by counter id. Each row carries enough context to show
  // which case it belongs to.
  const relevantCounters = useMemo(() => {
    const outgoingIds = new Set(outgoing.map((m) => m.id));
    const seen = new Map<string, { counter: Counter; intercepts?: Move; reason: 'against-my-move' | 'lands-here' }>();
    for (const c of counters) {
      const intercepts = moves.find((m) => m.id === c.intercepts);
      if (intercepts && outgoingIds.has(intercepts.id)) {
        seen.set(c.id, { counter: c, intercepts, reason: 'against-my-move' });
      } else if (c.result === position.id) {
        seen.set(c.id, { counter: c, intercepts, reason: 'lands-here' });
      }
    }
    return [...seen.values()];
  }, [counters, moves, outgoing, position.id]);
  return (
    <div>
      <div
        className="bjjmap-detail-header"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
      >
        {/* Same text-tag format as MoveDetail / CounterDetail:
              "{zone} · {difficulty} · {posture}"
            so the three detail-kind headers all share one visual
            language. Earlier this used coloured badge chips, which
            felt out of place sitting next to the dotted-text headers
            on the other detail kinds. */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontFamily: FONT.mono,
              fontSize: 11,
              textTransform: 'uppercase',
              letterSpacing: 0.7,
              color: INK3,
            }}
          >
            {position.zone.replace(/-/g, ' ')} ·{' '}
            {position.difficulty ?? 'unrated'} · {position.posture}
          </div>
          <h2
            style={{
              margin: '4px 0 8px 0',
              fontSize: 22,
              fontFamily: FONT.display,
              letterSpacing: '-0.01em',
              color: INK,
            }}
          >
            {position.name}
          </h2>
        </div>
        <DetailHeaderFeedback onFeedback={onFeedback} />
      </div>
      <MetaChips
        positionType={positionTypeForPosition(position)}
        advantage={(position as { dominance?: string }).dominance}
        family={position.family}
        concepts={position.concepts}
        tags={position.tags}
      />
      {/* Aliases sit with the tag rack, not after the prose. They are
          identity — the other names for this thing — so they belong
          beside the classification chips, before the description that
          explains it. Trailing the prose put a naming fact at the end of
          an explanation, where nobody reading for "what is this called"
          would look. */}
      {position.aliases && position.aliases.length > 0 && (
        <p style={{ fontSize: 12, opacity: 0.55 }}>aliases: {position.aliases.join(', ')}</p>
      )}
      <p style={{ color: INK2, fontSize: 14, lineHeight: 1.55 }}>{position.description}</p>
      <MediaCarousel refs={media} entityKey={`pos:${position.id}`} accent={accent ?? '#9be7ff'} />
      <MoveTable
        title="Moves From Here"
        direction="→"
        moves={outgoing}
        positions={positions}
        onSelect={(id) => setSelection({ kind: 'move', id })}
        onPreview={setPreviewMoveId}
        highlightedId={previewMoveId}
      />
      <MoveTable
        title="Moves To Here"
        direction="←"
        moves={incoming}
        positions={positions}
        onSelect={(id) => setSelection({ kind: 'move', id })}
        onPreview={setPreviewMoveId}
        highlightedId={previewMoveId}
      />
      {relevantCounters.length > 0 && (
        <Section title="Counters / Interceptions">
          {relevantCounters.map((entry) => {
            const c = entry.counter;
            const right =
              entry.reason === 'against-my-move' && entry.intercepts
                ? `vs ${entry.intercepts.name}`
                : `lands here from ${entry.intercepts?.name ?? c.intercepts}`;
            return (
              <Row
                key={c.id}
                left={c.name}
                right={right}
                onClick={() =>
                  entry.intercepts &&
                  setSelection({ kind: 'counter', id: c.id, moveId: entry.intercepts.id })
                }
              />
            );
          })}
        </Section>
      )}
    </div>
  );
}

function MoveDetail({
  move,
  counters,
  positions,
  moves,
  media,
  onFeedback,
}: {
  move: Move;
  counters: Counter[];
  positions: Position[];
  moves: Move[];
  media: MediaRef[];
  onFeedback?: () => void;
}) {
  const fromPos = positions.find((p) => p.id === move.from);
  const toPos = positions.find((p) => p.id === move.to);
  const setSelection = useAppStore((s) => s.setSelection);
  const setPreviewMoveId = useAppStore((s) => s.setPreviewMoveId);
  const previewMoveId = useAppStore((s) => s.previewMoveId);

  // Sleeve siblings: every other move that shares this move's (from, to).
  // When present, these surface ABOVE the follower/predecessor tables so
  // the user can quickly switch to a different option within the same
  // arc on the map.
  const sleeveSiblings = useMemo(
    () =>
      moves.filter(
        (x) =>
          x.id !== move.id && x.from === move.from && x.to === move.to,
      ),
    [moves, move],
  );

  // Moves that can be performed right after this one ends — their `from`
  // equals this move's `to`.
  const followers = useMemo(
    () => moves.filter((x) => x.from === move.to && x.id !== move.id),
    [moves, move],
  );
  // Moves that typically lead to this one — their `to` equals this
  // move's `from`.
  const predecessors = useMemo(
    () => moves.filter((x) => x.to === move.from && x.id !== move.id),
    [moves, move],
  );

  return (
    <div>
      <div
        className="bjjmap-detail-header"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT.mono, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: INK3 }}>
            {move.category} · {move.difficulty ?? 'unrated'} ·{' '}
            {prettyGi(move.gi)}
          </div>
          <h2 style={{ margin: '4px 0 8px 0', fontSize: 22, fontFamily: FONT.display, letterSpacing: '-0.01em', color: INK }}>{move.name}</h2>
        </div>
        <DetailHeaderFeedback onFeedback={onFeedback} />
      </div>
      {/* Transition line — the single most useful fact about a move /
          counter ("where does this take me?"), so it's sized up and
          given room rather than reading as fine print. */}
      <div
        style={{
          fontSize: 15.5,
          fontFamily: FONT.display,
          color: INK2,
          // Extra room beneath so the transition reads as its own
          // segment rather than running into the tag rack below.
          margin: '4px 0 20px',
          lineHeight: 1.45,
        }}
      >
        from{' '}
        <span
          className="bjjmap-link"
          onClick={() => fromPos && setSelection({ kind: 'position', id: fromPos.id })}
        >
          {fromPos?.name ?? move.from}
        </span>{' '}
        →{' '}
        <span
          className="bjjmap-link"
          onClick={() => toPos && setSelection({ kind: 'position', id: toPos.id })}
        >
          {toPos?.name ?? move.to}
        </span>
      </div>
      <MetaChips
        positionType={fromPos ? positionTypeForPosition(fromPos) : undefined}
        advantage={(move as { performedBy?: string }).performedBy}
        family={move.family}
        concepts={move.concepts}
        tags={move.tags}
      />
      {/* Aliases sit with the tag rack, not after the prose. They are
          identity — the other names for this thing — so they belong
          beside the classification chips, before the description that
          explains it. Trailing the prose put a naming fact at the end of
          an explanation, where nobody reading for "what is this called"
          would look. */}
      {move.aliases && move.aliases.length > 0 && (
        <p style={{ fontSize: 12, opacity: 0.55 }}>aliases: {move.aliases.join(', ')}</p>
      )}
      <p style={{ color: INK2, lineHeight: 1.55, fontSize: 14 }}>{move.description}</p>
      <MediaCarousel refs={media} entityKey={`move:${move.id}`} accent="#9be7ff" />
      <Section title="Steps">
        {move.steps.map((s, i) => (
          <div
            key={i}
            style={{ padding: '6px 0', borderBottom: `1px solid ${LINE}` }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{s.body}</div>
          </div>
        ))}
      </Section>
      {counters.length > 0 && (
        <Section title="Counters / interceptions">
          {counters.map((c) => {
            const resultPos = positions.find((p) => p.id === c.result);
            return (
              <Row
                key={c.id}
                left={c.name}
                right={`→ ${resultPos?.name ?? c.result}`}
                onClick={() => setSelection({ kind: 'counter', id: c.id, moveId: move.id })}
              />
            );
          })}
        </Section>
      )}
      {sleeveSiblings.length > 0 && (
        <MoveTable
          title="Other Moves in This Sleeve"
          direction="→"
          moves={sleeveSiblings}
          positions={positions}
          onSelect={(id) => setSelection({ kind: 'move', id })}
          onPreview={setPreviewMoveId}
          highlightedId={previewMoveId}
        />
      )}
      <MoveTable
        title="Moves That Can Follow"
        direction="→"
        moves={followers}
        positions={positions}
        onSelect={(id) => setSelection({ kind: 'move', id })}
        onPreview={setPreviewMoveId}
        highlightedId={previewMoveId}
      />
      <MoveTable
        title="Moves That Lead To This"
        direction="←"
        moves={predecessors}
        positions={positions}
        onSelect={(id) => setSelection({ kind: 'move', id })}
        onPreview={setPreviewMoveId}
        highlightedId={previewMoveId}
      />
    </div>
  );
}

function CounterDetail({
  counter,
  move,
  positions,
  moves,
  counters,
  media,
  onFeedback,
}: {
  counter: Counter;
  move: Move;
  positions: Position[];
  moves: Move[];
  counters: Counter[];
  media: MediaRef[];
  onFeedback?: () => void;
}) {
  const setSelection = useAppStore((s) => s.setSelection);
  const setPreviewMoveId = useAppStore((s) => s.setPreviewMoveId);
  const previewMoveId = useAppStore((s) => s.previewMoveId);
  const fromPos = positions.find((p) => p.id === counter.from);
  const result = positions.find((p) => p.id === counter.result);

  // Sibling counters of the same parent move — the analog of "other
  // moves in this sleeve". Lets the user pivot to a different defense
  // against the same attack without going back to the parent move.
  const siblingCounters = useMemo(
    () => counters.filter((c) => c.id !== counter.id && c.intercepts === counter.intercepts),
    [counters, counter],
  );

  // Moves that follow from where the counter lands. Same role as
  // MoveDetail's "Moves That Can Follow" — gives the user a continuation
  // path out of a successful counter.
  const followers = useMemo(
    () => moves.filter((x) => x.from === counter.result),
    [moves, counter.result],
  );

  // Counters that lead to the same result — sibling defenses arriving
  // at the same destination from different attacks. Useful for
  // recognizing recurring escape shapes (e.g., "lots of things end in
  // scrambled" or "lots of frame-and-recovers land in closed-guard-bottom").
  const counterPeers = useMemo(
    () => counters.filter((c) => c.id !== counter.id && c.result === counter.result),
    [counters, counter],
  );

  return (
    <div>
      <div
        className="bjjmap-detail-header"
        style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: FONT.mono, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.7, color: INK3 }}>
            counter · {counter.category} · {counter.difficulty ?? 'unrated'} ·{' '}
            {prettyGi(counter.gi)} · intercepts{' '}
            <span
              className="bjjmap-link"
              onClick={() => setSelection({ kind: 'move', id: move.id })}
            >
              {move.name}
            </span>
          </div>
          <h2 style={{ margin: '4px 0 8px 0', fontSize: 22, fontFamily: FONT.display, letterSpacing: '-0.01em', color: INK }}>{counter.name}</h2>
        </div>
        <DetailHeaderFeedback onFeedback={onFeedback} />
      </div>
      {/* Transition line — the single most useful fact about a move /
          counter ("where does this take me?"), so it's sized up and
          given room rather than reading as fine print. */}
      <div
        style={{
          fontSize: 15.5,
          fontFamily: FONT.display,
          color: INK2,
          // Extra room beneath so the transition reads as its own
          // segment rather than running into the tag rack below.
          margin: '4px 0 20px',
          lineHeight: 1.45,
        }}
      >
        from{' '}
        <span
          className="bjjmap-link"
          onClick={() => fromPos && setSelection({ kind: 'position', id: fromPos.id })}
        >
          {fromPos?.name ?? counter.from}
        </span>{' '}
        →{' '}
        <span
          className="bjjmap-link"
          onClick={() => result && setSelection({ kind: 'position', id: result.id })}
        >
          {result?.name ?? counter.result}
        </span>
      </div>
      <MetaChips
        positionType={fromPos ? positionTypeForPosition(fromPos) : undefined}
        family={counter.family}
        concepts={counter.concepts}
      />
      {counter.aliases && counter.aliases.length > 0 && (
        <p style={{ fontSize: 12, opacity: 0.55 }}>aliases: {counter.aliases.join(', ')}</p>
      )}
      <p style={{ color: INK2, lineHeight: 1.55, fontSize: 14 }}>{counter.description}</p>
      {typeof counter.probability === 'number' && (
        <p style={{ fontSize: 12, opacity: 0.55 }}>
          probability if attempted: {(counter.probability * 100).toFixed(0)}%
        </p>
      )}
      {counter.notes && (
        <p style={{ fontSize: 12, opacity: 0.65, marginTop: 4 }}>{counter.notes}</p>
      )}
      <MediaCarousel refs={media} entityKey={`counter:${counter.id}`} accent="#ffb280" />
      <Section title="Steps">
        {counter.steps.map((s, i) => (
          <div
            key={i}
            style={{ padding: '6px 0', borderBottom: `1px solid ${LINE}` }}
          >
            <div style={{ fontWeight: 600, fontSize: 13 }}>{s.title}</div>
            <div style={{ fontSize: 13, opacity: 0.8 }}>{s.body}</div>
          </div>
        ))}
      </Section>
      {siblingCounters.length > 0 && (
        <Section title={`Other Defenses Against ${move.name}`}>
          {siblingCounters.map((sc) => {
            const r = positions.find((p) => p.id === sc.result);
            return (
              <Row
                key={sc.id}
                left={sc.name}
                right={`→ ${r?.name ?? sc.result}`}
                onClick={() =>
                  setSelection({ kind: 'counter', id: sc.id, moveId: move.id })
                }
              />
            );
          })}
        </Section>
      )}
      <MoveTable
        title="Moves That Can Follow"
        direction="→"
        moves={followers}
        positions={positions}
        onSelect={(id) => setSelection({ kind: 'move', id })}
        onPreview={setPreviewMoveId}
        highlightedId={previewMoveId}
      />
      {counterPeers.length > 0 && (
        <Section title={`Other Defenses Landing in ${result?.name ?? counter.result}`}>
          {counterPeers.map((sc) => {
            const interceptedMove = moves.find((m) => m.id === sc.intercepts);
            return (
              <Row
                key={sc.id}
                left={sc.name}
                right={`vs ${interceptedMove?.name ?? sc.intercepts}`}
                onClick={() =>
                  setSelection({ kind: 'counter', id: sc.id, moveId: sc.intercepts })
                }
              />
            );
          })}
        </Section>
      )}
    </div>
  );
}

/**
 * Surface label for a move / counter's gi tag. The data model uses
 * 'both' as a value but the user-facing tag line reads cleaner as
 * 'gi / no-gi' — so the move applies in either ruleset. Missing values
 * fall back to the same label.
 */
function prettyGi(g: 'gi' | 'no-gi' | 'both' | undefined): string {
  if (g === 'gi') return 'gi';
  if (g === 'no-gi') return 'no-gi';
  return 'gi / no-gi';
}

/**
 * Mirror of cluster.ts:positionTypeForPosition() — duplicated here to
 * avoid a cross-cutting import for what is essentially a label utility.
 * If the cluster logic shifts, update both call sites.
 */
function positionTypeForPosition(p: Position): string | undefined {
  if (p.tags?.includes('symmetric')) return 'neutral';
  const z = p.zone;
  if (z === 'submitted') return 'terminal';
  if (z === 'scrambled') return 'flux';
  if (z === 'standing' || z === 'clinch') return 'neutral';
  if (z.endsWith('-top')) return 'top';
  if (z.endsWith('-bottom')) return 'bottom';
  return undefined;
}

/**
 * Compact chip rack showing the metadata the cluster system surfaces —
 * position type (top/bottom/neutral/flux/terminal), family, and concepts.
 * Each is rendered only when present. Chip colors line up with the
 * cluster palette so the drawer reinforces what the user sees on the
 * 3D graph.
 */
/**
 * Feedback button that sits at the top-right of a detail node header.
 * Shared by PositionDetail / MoveDetail / CounterDetail so the
 * "suggest a correction or addition" affordance lives right next to
 * the node name, not in the tab-switcher row. Renders nothing when
 * `onFeedback` is undefined (form not configured).
 */
function DetailHeaderFeedback({ onFeedback }: { onFeedback?: () => void }) {
  if (!onFeedback) return null;
  return (
    <button
      onClick={onFeedback}
      aria-label="Suggest a correction or addition to this node"
      title="Suggest a correction or addition"
      style={{
        flexShrink: 0,
        width: 32,
        height: 32,
        padding: 0,
        borderRadius: 8,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'rgba(255,255,255,0.06)',
        border: `1px solid ${LINE2}`,
        color: INK2,
        cursor: 'pointer',
      }}
    >
      {/* Pencil-in-note glyph. */}
      <svg
        width="15"
        height="15"
        viewBox="0 0 16 16"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.4"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        <path d="M11 2l3 3-8 8H3v-3z" />
      </svg>
    </button>
  );
}

/**
 * The single tag rack, above the description.
 *
 * Everything that classifies a node lives here: what kind of position it
 * is, who is winning, its family, its concepts, and the corpus hashtags.
 * They used to be split — coloured chips above the prose, corpus tags
 * below it, and for a while a spelled-out "family: x · concepts: y" line
 * in between that repeated two of the three. Nothing justified the split;
 * it just read as two unrelated racks with the description wedged
 * between them.
 */
function MetaChips({
  positionType,
  advantage,
  family,
  concepts,
  tags,
}: {
  positionType?: string;
  /** dominance on a position, performedBy on a move. */
  advantage?: string | null;
  family?: string;
  concepts?: string[];
  tags?: string[];
}) {
  const filterListByTag = useAppStore((s) => s.filterListByTag);
  const advStyle = advantage ? advantageStyle('#' + advantage) : undefined;
  const advLabel = advantage ? TAG_LABELS['#' + advantage] : undefined;
  // The advantage already has its own chip, so the mirrored #tori tag
  // would be the same fact twice in one row.
  const corpusTags = (tags ?? []).filter((t) => !isAdvantageTag(t));
  const hasAny =
    !!positionType || !!advLabel || !!family ||
    (concepts && concepts.length > 0) || corpusTags.length > 0;
  if (!hasAny) return null;

  const chipBase: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '2px 8px',
    borderRadius: 10,
    fontSize: 11,
    letterSpacing: 0.4,
  };

  // Pretty-print: 'top' → 'Top', 'closed-guard' → 'Closed Guard'.
  const pretty = (s: string) =>
    s
      .split(/[-_]/)
      .map((w) => (w.length <= 3 ? w.toUpperCase() : w.charAt(0).toUpperCase() + w.slice(1)))
      .join(' ');

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: 6,
        margin: '6px 0 10px',
        alignItems: 'center',
      }}
    >
      {positionType && (
        <span
          style={{
            ...chipBase,
            color: CLUSTER_COLORS[positionType] ?? '#cad3df',
            background: `${CLUSTER_COLORS[positionType] ?? '#888'}1a`,
            border: `1px solid ${CLUSTER_COLORS[positionType] ?? '#888'}55`,
            textTransform: 'uppercase',
          }}
          title="position type (cluster)"
        >
          {pretty(positionType)}
        </span>
      )}
      {advLabel && advStyle && (
        <span
          role="button"
          tabIndex={0}
          onClick={() => filterListByTag(`dom:${advantage}`)}
          style={{
            ...chipBase,
            cursor: 'pointer',
            color: advStyle.fg,
            background: advStyle.bg,
            border: `1px solid ${advStyle.border}`,
          }}
          title="Who is winning this exchange — filter the list by it"
        >
          <span style={{ opacity: 0.65, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            advantage
          </span>
          <span>{advLabel}</span>
          {/* The mat term, kept visible but subordinate. Without it the
              words tori and uke appear nowhere in the UI at all, so
              nobody can tell this chip IS that field. */}
          <span style={{ opacity: 0.45, fontSize: 10 }}>{advantage}</span>
        </span>
      )}
      {family && (
        <span
          role="button"
          tabIndex={0}
          onClick={() => filterListByTag(`family:${family}`)}
          style={{
            ...chipBase,
            cursor: 'pointer',
            color: clusterColorForId(family, 'family'),
            background: `${clusterColorForId(family, 'family')}1a`,
            border: `1px solid ${clusterColorForId(family, 'family')}55`,
          }}
          title="Filter the list by this family"
        >
          <span style={{ opacity: 0.65, fontSize: 10, textTransform: 'uppercase', letterSpacing: 0.6 }}>
            family
          </span>
          <span>{pretty(family)}</span>
        </span>
      )}
      {concepts && concepts.length > 0 && (
        <>
          <span
            style={{
              fontSize: 10,
              opacity: 0.45,
              textTransform: 'uppercase',
              letterSpacing: 0.6,
            }}
          >
            concepts
          </span>
          {concepts.map((c) => (
            <span
              key={c}
              role="button"
              tabIndex={0}
              onClick={() => filterListByTag(`concept:${c}`)}
              style={{
                ...chipBase,
                cursor: 'pointer',
                color: clusterColorForId(c),
                background: `${clusterColorForId(c)}14`,
                border: `1px solid ${clusterColorForId(c)}44`,
              }}
              title="Filter the list by this concept"
            >
              {pretty(c)}
            </span>
          ))}
        </>
      )}
      {/* Corpus hashtags last and muted. They are provenance — what the
          node was verified under — rather than a classification the
          reader acts on, so they trail the semantic chips instead of
          competing with them. */}
      {corpusTags.map((t) => (
        <span
          key={t}
          role="button"
          tabIndex={0}
          onClick={() => filterListByTag(`tag:${t}`)}
          style={{
            ...chipBase,
            cursor: 'pointer',
            borderRadius: 999,
            border: '1px solid rgba(255,255,255,0.14)',
            color: 'rgba(255,255,255,0.55)',
          }}
          title="Filter the list by this tag"
        >
          {t.startsWith('#') ? t : `#${t}`}
        </span>
      ))}
    </div>
  );
}

/**
 * Detail / List view switcher — Apple Finder style.
 *
 * Two equal-width tabs over a single absolutely-positioned "thumb"
 * (the rounded pill). Toggling the active view animates the thumb's
 * translateX between 0 and 100%, mirroring Finder's smooth slide. The
 * tab labels themselves don't move — they just brighten/dim as the
 * thumb passes under them. Pure CSS transform; no JS animation loop.
 *
 * Exported so both the Drawer's sticky nav bar AND ListView's header
 * render the identical control. `activeView` drives the thumb position;
 * clicking the inactive side flips `listViewOpen` and ListView's
 * useLayoutEffect handles the selection scroll-into-view on the other
 * side.
 *
 * Cross-panel slide trick: the drawer and list view are mutually
 * exclusive in App.tsx — when listViewOpen flips, the entire switcher
 * remounts in the new panel with the thumb already at its target
 * position, so a naive `setListViewOpen` would jump-cut. To keep the
 * animation continuous, the click handler stages the target via local
 * `pending` state (drives the thumb's transform immediately) and only
 * fires the actual `setListViewOpen` after the transition duration.
 * The new panel renders with the thumb already at its destination —
 * indistinguishable from a single continuous slide.
 */
const SWITCH_TRANSITION_MS = 240;

export type PanelView = 'detail' | 'list' | 'plan';

export function ViewSwitcher({
  activeView,
  hasSelection = true,
}: {
  activeView: PanelView;
  /** When false, the Detail tab is shown but disabled — there's no
   *  entity to detail. List + Plan stay fully interactive. */
  hasSelection?: boolean;
}) {
  const setListViewOpen = useAppStore((s) => s.setListViewOpen);
  const setGameplanViewOpen = useAppStore((s) => s.setGameplanViewOpen);
  // Onboarding: the search step flashes this switcher's Search tab.
  const coachHighlight = useAppStore((s) => s.coachHighlight);
  const [pending, setPending] = useState<PanelView | null>(null);
  const pendingTimerRef = useRef<number | null>(null);
  // Clean up any in-flight timer if this switcher unmounts mid-animation
  // (e.g. user clicks the edge caret to close everything during a swap).
  useEffect(
    () => () => {
      if (pendingTimerRef.current !== null) {
        window.clearTimeout(pendingTimerRef.current);
      }
    },
    [],
  );
  const effective = pending ?? activeView;
  const animating = pending !== null;

  // When there's no selection the Detail tab is hidden entirely, so the
  // switcher collapses to a 2-slot control. effectiveTab falls back to
  // the first available tab if the active view was 'detail' with nothing
  // selected.
  const tabs: PanelView[] = hasSelection
    ? ['detail', 'list', 'plan']
    : ['list', 'plan'];
  const effectiveTab = tabs.includes(effective) ? effective : tabs[0];
  const slotCount = tabs.length;
  const slotIdx = tabs.indexOf(effectiveTab);
  const TAB_META: Record<PanelView, { label: string; title: string }> = {
    detail: {
      label: 'Detail',
      title: 'Show the detail drawer for the current selection',
    },
    list: {
      label: 'Search',
      title: 'Search & filter positions, moves, and tags',
    },
    plan: { label: 'Plan', title: 'Open the gameplan editor' },
  };

  const switchTo = (target: PanelView) => {
    if (animating) return;
    if (target === effective) return;
    if (target === 'detail' && !hasSelection) return; // nothing to show
    setPending(target);
    pendingTimerRef.current = window.setTimeout(() => {
      pendingTimerRef.current = null;
      if (target === 'detail') {
        setListViewOpen(false);
        setGameplanViewOpen(false);
      } else if (target === 'list') {
        setListViewOpen(true);
      } else {
        setGameplanViewOpen(true);
      }
      setPending(null);
    }, SWITCH_TRANSITION_MS);
  };

  // ---- Universal back / forward ----
  //
  // Back is context-aware so a single button serves every panel:
  //   Detail   → pop selection history; close drawer when stack empty.
  //   List     → close panel.
  //   Plan     → step out of the active gameplan into the index; close
  //              panel when already at the index.
  // Forward only applies to Detail (selection history) — disabled in
  // List / Plan since those have no equivalent.
  const back = useAppStore((s) => s.back);
  const forward = useAppStore((s) => s.forward);
  const historyIndex = useAppStore((s) => s.historyIndex);
  const history = useAppStore((s) => s.history);
  const setDrawerOpen = useAppStore((s) => s.setDrawerOpen);
  const setListViewOpen2 = useAppStore((s) => s.setListViewOpen);
  const setGameplanViewOpen2 = useAppStore((s) => s.setGameplanViewOpen);
  const activeGameplanId = useAppStore((s) => s.activeGameplanId);
  const setActiveGameplan = useAppStore((s) => s.setActiveGameplan);

  const canForward = activeView === 'detail' && historyIndex < history.length - 1;
  const universalCanBack = (() => {
    if (activeView === 'detail') return true; // close drawer if no history
    if (activeView === 'list') return true; // always closable
    if (activeView === 'plan') return true; // step out of plan or close
    return false;
  })();
  const universalBack = () => {
    if (activeView === 'detail') {
      if (historyIndex > 0 || useAppStore.getState().listViewEntryIndex !== null || useAppStore.getState().gameplanViewEntryIndex !== null) {
        back();
      } else {
        setDrawerOpen(false);
      }
      return;
    }
    if (activeView === 'list') {
      setListViewOpen2(false);
      return;
    }
    if (activeView === 'plan') {
      if (activeGameplanId !== null) {
        setActiveGameplan(null);
      } else {
        setGameplanViewOpen2(false);
      }
      return;
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        width: '100%',
      }}
    >
      <div
        role="tablist"
        aria-label="Panel view"
        style={{
          position: 'relative',
          display: 'flex',
          flex: 1,
          minWidth: 0,
          background: 'rgba(0,0,0,0.32)',
          border: `1px solid ${LINE}`,
          borderRadius: RADIUS.pill,
          padding: 3,
          boxSizing: 'border-box',
          userSelect: 'none',
        }}
      >
        <div
          aria-hidden
          style={{
            position: 'absolute',
            top: 3,
            bottom: 3,
            left: 3,
            width: `calc((100% - 6px) / ${slotCount})`,
            background: hexA(BLUE, 0.14),
            borderRadius: RADIUS.pill,
            boxShadow:
              `0 1px 2px rgba(0,0,0,0.25), inset 0 0 0 1px ${hexA(BLUE, 0.32)}`,
            transform: `translateX(${slotIdx * 100}%)`,
            transition:
              'transform 240ms cubic-bezier(0.2, 0.8, 0.2, 1)',
            willChange: 'transform',
            pointerEvents: 'none',
          }}
        />
        {tabs.map((t) => (
          <FinderTab
            key={t}
            active={effectiveTab === t}
            onClick={() => switchTo(t)}
            title={TAB_META[t].title}
            label={TAB_META[t].label}
            // Plan mode is still rough — flag it as beta so
            // expectations are set. Drop this when it's polished.
            badge={t === 'plan' ? 'beta' : undefined}
            tourAnchor={t === 'list' ? 'search-tab' : undefined}
            flash={t === 'list' && coachHighlight === 'search'}
          />
        ))}
      </div>

      {/*
        Back / forward buttons are HIDDEN per user request (#15) — the
        functionality (universalBack / forward / canForward /
        universalCanBack and the underlying store actions) is left
        defined above so we can resurrect this row later without
        re-derivation.

        For now the bottom-right EdgeActions stack (App.tsx) exposes
        the two affordances the user actually wanted from this row:
        a deselect "×" when a node is selected and an "exit plan"
        door when viewing a gameplan. Both are mobile-thumb-friendly
        and slide up with the bottom sheet's resize handle.

        If we resurrect this row later, drop in a `<div>` with the
        existing NavIconBtns wired to {universalBack, forward,
        universalCanBack, canForward} as before.

        The {false && ...} below pins the symbols to JSX so TypeScript
        doesn't trip over "declared but never read" — a quiet no-op
        that documents intent more cleanly than // eslint-disable.
      */}
      {false &&
        (universalCanBack || canForward) &&
        [universalBack, forward, NavIconBtn]}
    </div>
  );
}

/** Small icon button used for the universal back/forward affordance.
 *  Disabled state is just opacity + cursor so the button still occupies
 *  its slot (no layout jitter when capability toggles).
 *
 *  Currently unused — the back/forward buttons it powered are hidden
 *  pending decision on whether to bring them back. Kept defined so
 *  resurrecting the row is a one-line change. */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function NavIconBtn({
  children,
  onClick,
  disabled,
  title,
  ...aria
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled: boolean;
  title: string;
  'aria-label': string;
}) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      aria-label={aria['aria-label']}
      style={{
        width: 30,
        height: 30,
        padding: 0,
        background: disabled ? 'rgba(255,255,255,0.02)' : 'rgba(255,255,255,0.06)',
        border: `1px solid ${LINE}`,
        borderRadius: RADIUS.sm,
        color: disabled ? 'rgba(255,255,255,0.25)' : INK,
        cursor: disabled ? 'not-allowed' : 'pointer',
        fontSize: 18,
        lineHeight: 1,
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 120ms ease, color 120ms ease',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Tab button — positioned: relative so it lays out by flex and stays
 * above the absolutely-positioned thumb in stacking order. No
 * background of its own (the thumb provides the pill); only the label
 * color transitions in sync with the thumb's slide.
 */
function FinderTab({
  active,
  onClick,
  title,
  label,
  badge,
  disabled = false,
  tourAnchor,
  flash,
}: {
  active: boolean;
  onClick: () => void;
  title: string;
  label: string;
  /** Optional small modifier shown after the label, e.g. "beta". */
  badge?: string;
  /** Greys out the tab and disables click — used for the Detail tab
   *  when there's no selection to show. */
  disabled?: boolean;
  /** `data-tour` hook so the onboarding coach can anchor to this tab. */
  tourAnchor?: string;
  /** Pulse during the coach step that points here. */
  flash?: boolean;
}) {
  return (
    <button
      role="tab"
      data-tour={tourAnchor}
      aria-selected={active}
      aria-disabled={disabled || undefined}
      onClick={disabled ? undefined : onClick}
      title={title}
      style={{
        position: 'relative',
        flex: 1,
        animation: flash
          ? 'bjjmap-ctrl-flash 2.2s ease-in-out infinite'
          : undefined,
        background: 'transparent',
        border: 'none',
        padding: '5px 14px',
        borderRadius: RADIUS.pill,
        color: disabled
          ? 'rgba(230,232,239,0.28)'
          : active
            ? INK
            : INK2,
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        cursor: disabled ? 'not-allowed' : active ? 'default' : 'pointer',
        transition: 'color 240ms cubic-bezier(0.2, 0.8, 0.2, 1)',
        zIndex: 1,
        outline: 'none',
      }}
      onMouseEnter={(e) => {
        if (!active && !disabled)
          (e.currentTarget as HTMLButtonElement).style.color = INK;
      }}
      onMouseLeave={(e) => {
        if (!active && !disabled)
          (e.currentTarget as HTMLButtonElement).style.color = INK2;
      }}
    >
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        {label}
        {badge && (
          <sup
            style={{
              fontSize: 8,
              lineHeight: 1,
              letterSpacing: 0.4,
              textTransform: 'uppercase',
              fontWeight: 700,
              padding: '1px 3px',
              borderRadius: 4,
              // Amber "beta" chip — subtle, reads as a modifier not a
              // warning.
              color: '#ffd9a0',
              background: 'rgba(255,200,120,0.16)',
              border: '1px solid rgba(255,200,120,0.3)',
              top: 0,
            }}
          >
            {badge}
          </sup>
        )}
      </span>
    </button>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginTop: 18 }}>
      <h3
        style={{
          fontFamily: FONT.mono,
          fontSize: 11,
          color: INK3,
          textTransform: 'uppercase',
          letterSpacing: 0.8,
          margin: '0 0 6px',
        }}
      >
        {title}
      </h3>
      {children}
    </div>
  );
}

function Row({ left, right, onClick }: { left: string; right?: string; onClick?: () => void }) {
  if (!onClick) {
    // Non-clickable variant (no hover affordance)
    return (
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 10,
          padding: '6px 8px',
          borderRadius: 4,
          fontSize: 13,
          background: 'rgba(255,255,255,0.02)',
          marginBottom: 3,
        }}
      >
        <span>{left}</span>
        {right && <span style={{ opacity: 0.55, fontSize: 11 }}>{right}</span>}
      </div>
    );
  }
  return (
    <div className="bjjmap-row" onClick={onClick}>
      <span>{left}</span>
      {right && <span style={{ opacity: 0.55, fontSize: 11 }}>{right}</span>}
    </div>
  );
}

/**
 * MediaCarousel renders the per-entity outlierdb clip carousel. One YouTube
 * iframe at a time (lazy — we never mount more than the current slide so the
 * drawer doesn't fight 6 concurrent video players when the user opens an
 * entity). entityKey resets the carousel to slide 0 when the selection
 * changes (we key on it so React unmounts/remounts cleanly).
 */
function MediaCarousel({
  refs,
  entityKey,
  accent,
}: {
  refs: MediaRef[];
  entityKey: string;
  accent: string;
}) {
  return <MediaCarouselInner key={entityKey} refs={refs} accent={accent} />;
}

function MediaCarouselInner({ refs, accent }: { refs: MediaRef[]; accent: string }) {
  const [idx, setIdx] = useState(0);
  if (refs.length === 0) {
    return (
      <div
        style={{
          marginTop: 10,
          height: 140,
          background: 'rgba(255,255,255,0.04)',
          border: `1px dashed ${LINE}`,
          borderRadius: 6,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 11,
          opacity: 0.5,
        }}
      >
        no media yet
      </div>
    );
  }
  const safeIdx = Math.min(idx, refs.length - 1);
  const cur = refs[safeIdx];
  // Wrap-around prev/next.
  const next = () => setIdx((i) => (i + 1) % refs.length);
  const prev = () => setIdx((i) => (i - 1 + refs.length) % refs.length);

  return (
    <div style={{ marginTop: 10 }}>
      {/* Video frame */}
      <div
        style={{
          position: 'relative',
          width: '100%',
          aspectRatio: '16 / 9',
          background: 'rgba(0,0,0,0.5)',
          borderRadius: 8,
          overflow: 'hidden',
          border: `1px solid ${accent}33`,
        }}
      >
        <iframe
          // key forces a remount when slide changes — cleaner than relying on
          // src changes alone since YouTube's iframe ignores some src updates.
          key={cur.youtubeId + ':' + cur.startSec}
          src={cur.embedUrl}
          title={`outlierdb clip ${safeIdx + 1} / ${refs.length}`}
          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
          allowFullScreen
          loading="lazy"
          style={{
            position: 'absolute',
            inset: 0,
            width: '100%',
            height: '100%',
            border: 0,
          }}
        />
      </div>

      {/* Metadata + nav bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 8,
          marginTop: 6,
          fontSize: 11,
          fontFamily: FONT.mono,
          color: INK2,
        }}
      >
        {/* Resource / footage badge */}
        <span
          style={{
            padding: '2px 6px',
            borderRadius: 3,
            background: cur.isResource ? '#7dc48a22' : '#a3a8b022',
            border: `1px solid ${cur.isResource ? '#7dc48a55' : '#a3a8b055'}`,
            color: cur.isResource ? '#7dc48a' : 'rgba(230,232,239,0.7)',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            fontSize: 9,
            fontWeight: 600,
          }}
        >
          {cur.isResource ? 'instructional' : 'match footage'}
        </span>
        <span>@ {formatTimestamp(cur.startSec)}</span>
        <a
          href={cur.watchUrl}
          target="_blank"
          rel="noreferrer"
          style={{ color: accent, textDecoration: 'none', opacity: 0.8 }}
        >
          open on youtube ↗
        </a>
        <span style={{ flex: 1 }} />
        {refs.length > 1 && (
          <>
            <button
              onClick={prev}
              aria-label="Previous clip"
              style={mediaNavBtnStyle}
              title="Previous clip"
            >
              ‹
            </button>
            <span style={{ minWidth: 32, textAlign: 'center', opacity: 0.6 }}>
              {safeIdx + 1} / {refs.length}
            </span>
            <button
              onClick={next}
              aria-label="Next clip"
              style={mediaNavBtnStyle}
              title="Next clip"
            >
              ›
            </button>
          </>
        )}
      </div>

      {/* Note excerpt (one of the outlier annotators' descriptions). */}
      {cur.note && (
        <div
          style={{
            marginTop: 6,
            padding: '8px 10px',
            background: 'rgba(255,255,255,0.03)',
            border: `1px solid ${LINE}`,
            borderRadius: 6,
            fontSize: 12,
            lineHeight: 1.5,
            opacity: 0.85,
            maxHeight: 110,
            overflowY: 'auto',
          }}
        >
          {cur.note}
        </div>
      )}

      {/* Provenance — keep it small but visible. */}
      <div style={{ marginTop: 4, fontSize: 10, opacity: 0.4 }}>
        sourced from outlierdb.com
      </div>
    </div>
  );
}

function formatTimestamp(seconds: number): string {
  const s = Math.max(0, Math.floor(seconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(sec).padStart(2, '0')}`;
  return `${m}:${String(sec).padStart(2, '0')}`;
}

const mediaNavBtnStyle: React.CSSProperties = {
  background: 'rgba(255,255,255,0.05)',
  color: INK,
  border: `1px solid ${LINE}`,
  borderRadius: 4,
  width: 24,
  height: 22,
  padding: 0,
  cursor: 'pointer',
  fontSize: 14,
  lineHeight: 1,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
